import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAdminNotifications } from '../api';
import {
  getReadNotificationIds,
  markAllNotificationsRead,
  markNotificationRead,
} from '../utils/adminNotificationsStorage';

const POLL_MS = 45000;

function formatWhen(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' });
}

export function useAdminNotifications({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [readVersion, setReadVersion] = useState(0);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await getAdminNotifications();
      if (!mountedRef.current) return;
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      setSummary(data?.summary || null);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err);
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchNotifications();
    const timer = window.setInterval(() => fetchNotifications({ silent: true }), POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, fetchNotifications]);

  const readIds = useMemo(() => getReadNotificationIds(), [readVersion, items]);

  const enriched = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        when: formatWhen(item.created_at),
        unread: !readIds.has(String(item.id)),
      })),
    [items, readIds],
  );

  const unreadCount = useMemo(
    () => enriched.filter((item) => item.unread).length,
    [enriched],
  );

  const markRead = useCallback((id) => {
    markNotificationRead(id);
    setReadVersion((v) => v + 1);
  }, []);

  const markAllRead = useCallback(() => {
    markAllNotificationsRead(items.map((item) => item.id));
    setReadVersion((v) => v + 1);
  }, [items]);

  return {
    notifications: enriched,
    summary,
    unreadCount,
    loading,
    error,
    refresh: fetchNotifications,
    markRead,
    markAllRead,
  };
}
